import {
  memo,
  forwardRef,
  FunctionComponent,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { Button } from '~ui/button/button';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { useQuery, gql, DocumentNode } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { HistoryInput as HistoryInputTypes, ActiveGames, Game } from '~types';

import { toast } from 'react-toastify';
import { useConfig } from '~components/hooks';
import Item from './Item';
import { SellAllItems } from '../index';
import playAudio from '~frontend/utils/playAudio';
import { Loader } from '../../Loader';
import IconEmpty from '../../../scss/images/icon-profile-empty.svg';
import { useToggle, useUpdateEffect } from 'react-use';
import { copyToClipboard } from '~frontend/utils/clickboard';
import { useItemsPerRow } from '../hooks/useItemsPerRow';
import './profileItemsStyles.scss';

export const FRAGMENT_ITEM_FIELDS = gql`
  fragment ItemFields on Item {
    id
    color
    getImage
    getName
    price
    isStatTrak
    quality
  }
`;

export const FRAGMENT_GAME_FIELDS = gql`
  fragment GameFields on Game {
    id
    status
    getStatus
    price
    priceSell
    userId
    chance
    type
    eventId
    battlePassId
    distributionGameId
    getItem {
      ...ItemFields
    }
    getCase {
      id
      getName
      getUrl
      getImage
      type
      seo
    }
  }

  ${FRAGMENT_ITEM_FIELDS}
`;

interface Types {
  query: DocumentNode;
  fragments: {
    games: DocumentNode;
  };
}

export const GET_HISTORY: Types = {
  query: gql`
    query GetHistory($input: HistoryInput!) {
      getHistory(input: $input) {
        count
        getGames {
          ...GameFields
        }
      }
    }

    ${FRAGMENT_GAME_FIELDS}
  `,
  fragments: {
    games: FRAGMENT_GAME_FIELDS,
  },
};

interface ItemsType {
  isUser?: boolean;
  userId: number;
  filters?: any;
}

interface ItemsData {
  getHistory: ActiveGames;
  getStats: Stats;
}

interface ItemsVar {
  input: HistoryInputTypes;
}

interface IItemsList {
  count: number;
  loading: boolean;
  isUser: boolean;
  getGames: Game[];
}

const ItemsListBase = forwardRef<HTMLDivElement, IItemsList>(
  ({ count, loading, getGames, isUser }, ref) => {
    const { t } = useTranslation();
    const historyItems = useMemo(
      () =>
        _.map(getGames, (getGame) => (
          <Item getGame={getGame} key={getGame?.id} isUser={isUser} />
        )),
      [getGames, isUser],
    );

    return (
      <div className="profile_items_grid" ref={ref}>
        {loading ? (
          <div style={{ margin: '0 auto' }}>
            <Loader />
          </div>
        ) : count === 0 ? (
          <div className="in-case-empty">
            <div className="empty">
              <img src={IconEmpty} alt="" />
              <div className="text">
                {t('Its time to beat out the top skins!')}
                <br />
                {t('For this, only')}
              </div>
              <Link className="btn btn-violet" to="/case/free">
                {t('Open free case')}
              </Link>
            </div>
          </div>
        ) : (
          <>{historyItems}</>
        )}
      </div>
    );
  },
);

const ItemsList = memo(ItemsListBase);

export const Items: FunctionComponent<ItemsType> = memo(
  ({ isUser, userId, filters }: ItemsType) => {
    const [isAwait, toggle] = useToggle(false);
    const [isCopy, toggleCopy] = useToggle(false);
    const { t } = useTranslation();
    const getConfig = useConfig();
    const profileUrl = `https://${getConfig?.hostname}/user/${userId}`;

    // ---------- Responsive batch size ----------
    const gridRef = useRef<HTMLDivElement>(null);
    const itemsPerRow = useItemsPerRow(gridRef) || 1;
    const rowsBatch = 3;
    const batchSize = itemsPerRow * rowsBatch;
    // -------------------------------------------

    const { loading, error, data, fetchMore, networkStatus, refetch } =
      useQuery<ItemsData, ItemsVar>(GET_HISTORY.query, {
        notifyOnNetworkStatusChange: true,
        variables: {
          input: {
            offset: 0,
            limit: batchSize,
            userId,
            isAwait,
            ...filters,
          },
        },
      });

    // ---------- Local state ----------
    /**  Используем синхронные данные из Apollo cache (SSR ➜ CSR),
     *   чтобы на клиенте первый рендер отрисовал ровно то же количество
     *   элементов, что уже пришло c сервера; иначе React фиксирует mismatch.
     */
    const initialGames = data?.getHistory?.getGames ?? [];
    const [allGames, setAllGames] = useState<Game[]>(() => initialGames);
    const [limit, setLimit] = useState(() =>
      Math.max(batchSize, initialGames.length),
    ); // сколько можно выводить
    // ---------------------------------

    // -------- Sync games returned from server --------
    useEffect(() => {
      if (data?.getHistory?.getGames) {
        setAllGames(data.getHistory.getGames);
        setLimit((prev) => Math.max(prev, data.getHistory.getGames.length));
      }
    }, [data]);
    // -------------------------------------------------

    // --------- Re‑request when user / filters change ----------
    const debouncedRefetch = useMemo(
      () =>
        _.debounce(
          (input) =>
            refetch({ input }).then((res) =>
              setAllGames(res.data.getHistory.getGames),
            ),
          300,
        ),
      [refetch],
    );

    useUpdateEffect(() => {
      debouncedRefetch({
        offset: 0,
        limit: batchSize,
        userId,
        isAwait,
        ...filters,
      });
      setLimit(batchSize); // сбрасываем окно показа, но не ниже уже загруженного
    }, [userId, isAwait, filters]);
    // ---------------------------------------------------------

    // --------- Responsive resize: подгружаем, но не сбрасываем -----------
    useUpdateEffect(() => {
      if (batchSize > limit && allGames.length < batchSize) {
        // Нужен дополнительный fetch, чтобы заполнить новый «экран»
        loadMore(batchSize - allGames.length);
      }
      setLimit((prev) => Math.max(prev, batchSize));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batchSize]);
    // ---------------------------------------------------------------------

    // ----------------- Клиентские фильтры -----------------
    const filteredGames = useMemo(() => {
      if (!allGames.length) return allGames;

      let updatedGames = [...allGames];

      // Фильтрация по типу
      if (filters?.category && filters.category !== 'All') {
        updatedGames = updatedGames.filter(
          (game) =>
            game.getItem.getName.split('|')[0].trim().toLowerCase() ===
            filters.category.toLowerCase(),
        );
      }

      // Фильтрация по цене
      if (filters?.minPrice) {
        updatedGames = updatedGames.filter(
          (game) => game.price >= filters.minPrice * 100,
        );
      }

      if (filters?.maxPrice) {
        updatedGames = updatedGames.filter(
          (game) => game.price <= filters.maxPrice * 100,
        );
      }

      // Только готовые к продаже
      if (filters?.forSell) {
        updatedGames = updatedGames.filter((game) => game.status === 1);
      }

      // Сортировка
      if (filters?.sortOrder === 'asc') {
        updatedGames = updatedGames.sort((a, b) => a.price - b.price);
      } else if (filters?.sortOrder === 'desc') {
        updatedGames = updatedGames.sort((a, b) => b.price - a.price);
      }

      return updatedGames;
    }, [allGames, filters]);
    // ------------------------------------------------------

    // ------------- Подгрузка следующей пачки ----------------
    const loadMore = async (expectedCount = batchSize) => {
      try {
        const { data: fmResult } = await fetchMore<ItemsData, ItemsVar>({
          variables: {
            input: {
              offset: allGames.length,
              limit: expectedCount,
              userId,
              isAwait,
              ...filters,
            },
          },
          updateQuery: (prev, { fetchMoreResult }) =>
            !fetchMoreResult
              ? prev
              : {
                  ...prev,
                  getHistory: {
                    ...prev.getHistory,
                    count: fetchMoreResult.getHistory.count,
                    getGames: _.uniqBy(
                      [
                        ...prev.getHistory.getGames,
                        ...fetchMoreResult.getHistory.getGames,
                      ],
                      'id',
                    ),
                  },
                },
        });

        setAllGames((prev) =>
          _.uniqBy([...prev, ...fmResult.getHistory.getGames], 'id'),
        );
        setLimit((prev) => prev + fmResult.getHistory.getGames.length);

        document
          .querySelector('.footer')
          ?.scrollIntoView({ block: 'end', behavior: 'smooth' });
      } catch (e: any) {
        toast.error(
          <>
            <div className="notify-title">{t('Error')}!</div>
            <div className="notify-text">{t(e.message)}</div>
          </>,
        );
      }
    };
    // --------------------------------------------------------

    const onClickCopy = () => {
      copyToClipboard(profileUrl);
      toggleCopy();
      setTimeout(() => toggleCopy(false), 1000);
    };

    if (error) {
      return <>Items | Error! {error.message}</>;
    }

    const count = data?.getHistory?.count ?? 0;
    const hasMore = count > allGames.length;

    return (
      <>
        <div className="profile_items">
          {!isUser && (
            <div className="sellAll_btn_mobile">
              <SellAllItems userId={userId} />
            </div>
          )}

          {isUser && <div className="profile_heading_mobile">{t('loot')}</div>}

          <ItemsList
            ref={gridRef}
            loading={loading && _.includes([2], networkStatus)}
            count={filteredGames.length}
            getGames={filteredGames.slice(0, limit)}
            isUser={isUser}
          />
        </div>

        {hasMore && (
          <div className="profile_load_more">
            <Button
              data-audio="6.mp3"
              onMouseEnter={playAudio}
              className="btn1_dark_bordered profile_load_more_btn"
              onClick={() => loadMore()}
              text={!loading && <>{t('Show more')}</>}
            />
          </div>
        )}
      </>
    );
  },
);
